import type { ApiClient } from '@carbonwebmail/api-client'
import type { InvitationPreviewDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * Accepting an invitation.
 *
 * The invitation is loaded before anything else, so the page can say which
 * organisation and which address before asking anybody to sign in — an
 * invitation is exactly the shape of a phishing message, and a page that
 * demanded credentials first and explained itself afterwards would deserve to
 * be closed.
 *
 * An undefined invitation is not a load failure. Expired, already used and
 * never existed all arrive here the same way, deliberately: the page can say
 * nothing useful about the difference, and telling them apart would tell
 * whoever is guessing tokens which guesses are worth refining.
 */

export type JoinState = AsyncState<InvitationPreviewDto | undefined>

export function useJoin({ client, token }: { readonly client: ApiClient; readonly token: string }) {
  const load = useCallback(() => client.invitation(token), [client, token])
  const { state } = useLoaded<InvitationPreviewDto | undefined>(
    load,
    'We could not check that invitation. Please try again in a moment.',
  )

  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [joining, setJoining] = useState(false)

  /**
   * Resolves true when there is a membership to go to.
   *
   * Nothing is sent: the name and the address come from the identity provider
   * and the role from the invitation, so there is nothing for this page to
   * choose. The caller's job afterwards is to re-read the session.
   */
  const join = useCallback(async () => {
    setJoining(true)
    setProblem(undefined)
    try {
      const outcome = await client.acceptInvitation(token)
      if (outcome.status === 'joined') return true

      setProblem(
        outcome.status === 'wrong-address'
          ? `This invitation is for ${outcome.invited ?? 'a different address'}. Sign out and sign back in as that address.`
          : outcome.status === 'address-taken'
            ? 'That address has been used for an account since the invitation was sent. Sign in with it instead.'
            : 'That invitation has expired or has already been used. Ask for a new one.',
      )
      return false
    } catch {
      setProblem('We could not complete that just now. Please try again in a moment.')
      return false
    } finally {
      setJoining(false)
    }
  }, [client, token])

  return { state, problem, joining, join }
}
